import React from "react";
import moment from "moment";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import PlaybookEditService from "../../services/PlaybookEditService";
import UtilityService from "../../services/UtilityService";
import PlaybookChangeModal from "./PlaybookChangeModal";

import "./playbook-versions-panel.css";

class PlaybookVersionsPanel extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			versions: [],
			isLoading: true,
			loadError: null,
			versionToRestore: null,
			lastRollBackNote: null,
		};
	}

	componentDidMount() {
		this.fetchVersions();
	}

	componentDidUpdate(previousProps) {
		if (previousProps.currentVersionId !== this.props.currentVersionId) this.fetchVersions();
	}

	fetchVersions = async () => {
		const { playbookId } = this.props;
		try {
			const versions = await PlaybookEditService.listVersions({ playbookId });
			await this.update({ versions, isLoading: false, loadError: null });
		} catch (error) {
			await this.update({ isLoading: false, loadError: "The version history could not be loaded." });
		}
	};

	onConfirmRollBack = async ({ changeNote }) => {
		const { playbookId, onChanged } = this.props;
		const { versionToRestore } = this.state;
		try {
			const result = await PlaybookEditService.rollBack({ playbookId, toVersionId: versionToRestore.id, changeNote });
			const keptNote =
				result.keptAdditionAuthors.length > 0
					? `Additions by ${result.keptAdditionAuthors.join(", ")} were kept — only their authors can remove them.`
					: null;
			await this.update({ versionToRestore: null, lastRollBackNote: keptNote });
			onChanged({ agent: result.agent });
			return null;
		} catch (error) {
			return UtilityService.describeRequestError({ error, fallback: "The roll back did not save." });
		}
	};

	renderChanges({ version }) {
		const lines = [];
		if (version.baseChanged)
			lines.push(
				<li key="base" className="playbook-versions__change playbook-versions__change--base">
					Base instructions changed (private)
				</li>
			);
		version.added.forEach(addition =>
			lines.push(
				<li key={`a-${addition.id}`} className="playbook-versions__change playbook-versions__change--added">
					<Icon.Plus size={12} /> <span>{addition.text}</span> <span className="playbook-versions__by">— {addition.author}</span>
				</li>
			)
		);
		version.edited.forEach(change =>
			lines.push(
				<li key={`e-${change.after.id}`} className="playbook-versions__change playbook-versions__change--edited">
					<Icon.Edit2 size={12} />
					<span>
						<del>{change.before.text}</del> → {change.after.text}
					</span>
					<span className="playbook-versions__by">— {change.after.author}</span>
				</li>
			)
		);
		version.removed.forEach(addition =>
			lines.push(
				<li key={`r-${addition.id}`} className="playbook-versions__change playbook-versions__change--removed">
					<Icon.Minus size={12} /> <del>{addition.text}</del> <span className="playbook-versions__by">— {addition.author}</span>
				</li>
			)
		);
		if (lines.length === 0) return null;
		return <ul className="playbook-versions__changes">{lines}</ul>;
	}

	renderWaivedGaps({ version }) {
		if (version.waivedGaps.length === 0) return null;
		return (
			<ul className="playbook-versions__changes">
				{version.waivedGaps.map(gap => (
					<li key={`g-${gap.gapId}`} className="playbook-versions__change playbook-versions__change--waived-gap">
						<Icon.AlertCircle size={12} />
						<span>
							Built without: {gap.whatItNeeds} <span className="playbook-versions__gap-step">({gap.step})</span> — {gap.reason}
						</span>
						<span className="playbook-versions__by">
							— waived by {gap.waivedBy.name || "someone no longer listed"}, {moment(gap.waivedAt).format("MMM D, YYYY")}
						</span>
					</li>
				))}
			</ul>
		);
	}

	renderVersion = (version, index) => {
		const isCurrent = index === 0;
		return (
			<div key={version.id} className="playbook-versions__version">
				<div className="playbook-versions__head">
					<span className="playbook-versions__number">v{version.versionNumber}</span>
					<span className="playbook-versions__kind">{PlaybookEditService.describeChangeKind({ changeKind: version.changeKind })}</span>
					{isCurrent && <span className="tk-badge tk-badge--accent">Current</span>}
					<span className="playbook-versions__who">
						{version.savedBy || "—"} · {moment(version.savedAt).format("MMM D, YYYY h:mm a")}
					</span>
					{!isCurrent && (
						<button
							type="button"
							className="tk-text-action tk-text-action--muted playbook-versions__restore"
							onClick={() => this.update({ versionToRestore: version })}
						>
							<Icon.RotateCcw size={12} />
							Roll back to this
						</button>
					)}
				</div>
				{version.changeNote && <div className="playbook-versions__note">{version.changeNote}</div>}
				{this.renderChanges({ version })}
				{this.renderWaivedGaps({ version })}
			</div>
		);
	};

	render() {
		const { versions, isLoading, loadError, versionToRestore, lastRollBackNote } = this.state;
		if (isLoading) return <div className="tk-empty tk-card">Loading versions…</div>;
		if (loadError) return <div className="tk-notice tk-notice--danger">{loadError}</div>;

		return (
			<div className="playbook-versions">
				{lastRollBackNote && (
					<div className="tk-notice">
						<div className="tk-notice__title">
							<Icon.Info size={14} />
							{lastRollBackNote}
						</div>
					</div>
				)}
				<div className="tk-card">{versions.map(this.renderVersion)}</div>
				{versionToRestore && (
					<PlaybookChangeModal
						key={versionToRestore.id}
						open={true}
						title={`Roll back to v${versionToRestore.versionNumber}`}
						intro="A new version is saved with your additions as they were then. Other people's additions stay as they are now — only their authors can remove them. Nothing is deleted."
						confirmLabel="Roll back"
						onClose={() => this.update({ versionToRestore: null })}
						onConfirm={this.onConfirmRollBack}
					/>
				)}
			</div>
		);
	}
}

export default PlaybookVersionsPanel;
